import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Section,
  Container,
  TitleWrapper,
  ContentWrapper,
  LeftContainer,
  VersionText,
  HighlightText,
  VideoContainer,
  Video,
  RightContainer,
  RightText,
  SubTitle,
  MainTitle,
  TitleSpan,
  HawkIcon,
  SvgContainer,
} from "../styles/sections/IntroducingSectionStyles";

const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
const finalText = "Titan's tap";

function IntroducingSection() {
  const highlightRef = useRef(null);
  const frameRef = useRef();
  const iterationRef = useRef(0);
  const frameCountRef = useRef(0);
  const isAnimatingRef = useRef(false);

  const scrambleText = () => {
    if (!highlightRef.current || !isAnimatingRef.current) return;

    // 프레임 카운트를 증가시키고, 3프레임마다 한 번씩만 텍스트 업데이트
    frameCountRef.current++;
    if (frameCountRef.current % 3 !== 0) {
      frameRef.current = requestAnimationFrame(scrambleText);
      return;
    }

    let result = "";
    let complete = true;
    const currentLength = Math.min(iterationRef.current + 1, finalText.length);

    // 현재 길이만큼 스크램블된 텍스트 생성
    for (let i = 0; i < currentLength; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
      complete = false;
    }

    // 나머지 공간은 빈칸으로 채움
    for (let i = currentLength; i < finalText.length; i++) {
      result += " ";
    }

    // 모든 글자가 스크램블되고 나서 한번 더 반복하면 최종 텍스트로 변경
    if (iterationRef.current > finalText.length) {
      result = finalText;
      complete = true;
    }

    highlightRef.current.textContent = result;

    if (!complete && isAnimatingRef.current) {
      iterationRef.current++;
      frameRef.current = requestAnimationFrame(scrambleText);
    } else {
      if (isAnimatingRef.current) {
        highlightRef.current.textContent = finalText;
      }
      isAnimatingRef.current = false;
      iterationRef.current = 0;
      frameCountRef.current = 0;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              isAnimatingRef.current = true;
              iterationRef.current = 0;
              frameCountRef.current = 0;
              scrambleText();
            }, 500);
          }
        });
      },
      {
        threshold: 0.3,
      }
    );

    if (highlightRef.current) {
      highlightRef.current.textContent = "";
      observer.observe(highlightRef.current);
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <Section id="introducing">
      <Container>
        <TitleWrapper>
          <SubTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Introducing
          </SubTitle>

          <MainTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <TitleSpan>Play the game and unleash the divine </TitleSpan>
            <br />
            <TitleSpan>power of the gods. </TitleSpan>
            <br />
            Each god provides a powerful ability to change the course of battle
            and your hero can win in the name of Olympus
            <br />
          </MainTitle>

          <HawkIcon
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <SvgContainer>
              <svg viewBox="0 0 50 28.09" id="svg10798193301">
                <g id="ss10798193301_1">
                  <path
                    d="M 27.607 12.118 L 26.601 10.478 L 24.91 9.904 L 23.219 10.478 L 22.213 12.118 L 24.91 19.022 Z M 24.91 14.629 L 23.404 12.056 L 24.904 13.225 L 26.404 12.056 Z"
                    fill="rgb(232, 228, 216)"
                  />
                  <path
                    d="M 47.146 7.612 L 29.393 14.758 L 45.77 6.247 L 49.815 0 L 29.264 12.983 L 26.528 20.32 L 25.545 22.972 L 24.882 21.674 L 24.258 22.944 L 23.281 20.32 L 20.551 12.983 L 0 0 L 4.039 6.247 L 20.421 14.758 L 2.663 7.612 L 6.567 12.416 L 20.876 16.337 L 4.73 13.663 L 8.826 17.73 L 21.36 17.747 L 8.326 19.163 L 12.758 21.742 L 21.753 19.197 L 13.23 23.067 L 18.303 23.388 L 22.506 20.663 L 19.663 28.213 L 23.685 25.517 L 24.882 28.213 L 26.118 25.506 L 30.152 28.213 L 27.309 20.663 L 31.506 23.388 L 36.584 23.067 L 28.062 19.197 L 37.051 21.742 L 41.483 19.163 L 28.449 17.747 L 40.989 17.73 L 45.079 13.663 L 28.938 16.337 L 43.247 12.416 Z"
                    fill="rgb(232, 228, 216)"
                  />
                </g>
              </svg>
            </SvgContainer>
          </HawkIcon>
        </TitleWrapper>

        <ContentWrapper>
          <LeftContainer
            data-framer-appear-id="1rgnueb"
            data-framer-name="Intro"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <VersionText>Demo 1st version</VersionText>
            <HighlightText ref={highlightRef}></HighlightText>
          </LeftContainer>

          <VideoContainer>
            <Video
              src="https://framerusercontent.com/assets/XkQUKpVuiCYycDsGD2KUvco0.mp4"
              loop
              preload="auto"
              muted
              playsInline
              autoPlay
            />
          </VideoContainer>

          <RightContainer
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <RightText>
              It may be changed later
              <br />
              Please refer to it briefly
            </RightText>
          </RightContainer>
        </ContentWrapper>
      </Container>
    </Section>
  );
}

export default IntroducingSection;
